import {
  Box,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { API_CLIENTS_ADVERTISERS_LIST_URL } from '../../constants/apiUrls';
import { CLIENTS_ADVERTISERS_EDIT_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import { getRequestJSON } from '../../util/services';
import { headerStyles } from '../Contracts/ContractsTable';

interface AdvertisersListProps {
  clientId: string;
}

function AdvertisersList(props: AdvertisersListProps) {
  // Advertisers index opts into the per-domain flip (ACH-colk). The
  // {collection,pagination} envelope is shape-identical across both backends for
  // the fields this table reads (collection[].id/name + pagination.total_items/
  // per_page), so only the host switches (RD-3 pagination meta is unread here).
  const { apiHostFor } = useConfig();
  const advertisersHost = apiHostFor('advertisers');
  const [advertisersList, setAdvertisersList] = useState([]);
  const [paginationState, setPagination] = useState({
    total_items: 0,
    per_page: 5,
  });
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const { clientId } = props;

  useEffect(() => {
    const getItems = async () => {
      const path = generatePath(API_CLIENTS_ADVERTISERS_LIST_URL, {
        id: clientId,
        '*': `?${queryString.stringify({
          // Server page is 1-index and front-end is 0-index based.
          page: `${page + 1}`,
        })}`,
      });
      setIsLoading(true);
      const response = await getRequestJSON(advertisersHost, path);
      setIsLoading(false);

      if (!response) {
        return;
      }

      const { collection, pagination: paginationInfo } = response;

      setAdvertisersList(collection);
      setPagination(paginationInfo);
    };

    getItems();
  }, [page, advertisersHost, clientId]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const headerStylesCss = headerStyles();

  return (
    <Box py={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
        <Stack direction="row" alignItems="center">
          <Typography variant="h5">Advertisers</Typography>
        </Stack>
      </Box>
      <Box mt={2}>
        <TableContainer className={headerStylesCss.root}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead sx={{}}>
              <TableRow>
                <TableCell>Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Loading Skeleton */}
              {isLoading &&
                [...Array(50)].map((item, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell scope="row">
                      <Skeleton animation="wave" width={500} variant="text" sx={{ fontSize: '1rem' }} />
                    </TableCell>
                    <TableCell align="left">
                      <Skeleton animation="wave" width={87} variant="text" sx={{ fontSize: '1rem' }} />
                    </TableCell>
                  </TableRow>
                ))}
              {advertisersList &&
                advertisersList.length > 0 &&
                advertisersList.map((advertiser) => (
                  <TableRow
                    key={`link-${advertiser.id}`}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    onClick={() =>
                      navigate(
                        generatePath(CLIENTS_ADVERTISERS_EDIT_URL, {
                          id: clientId,
                          advertiserId: advertiser.id,
                        })
                      )
                    }
                  >
                    <TableCell scope="row">
                      {advertiser.name} ({advertiser.id})
                    </TableCell>
                  </TableRow>
                ))}
              <TableRow key="pagination">
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  count={paginationState.total_items} // served static by backend
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={paginationState.per_page} // served static by backend
                  onRowsPerPageChange={() => {}}
                />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

export default AdvertisersList;
